import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import SmoothScroll from "./components/SmoothScroll";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { MobileCtaBar } from "./components/MobileCtaBar";
// Home stays eager (LCP page). Everything else code-splits to shrink the initial bundle
// on the mobile-critical first-paint path.
import Home from "./pages/Home";

const Services = lazy(() => import("./pages/Services"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const About = lazy(() => import("./pages/About"));
const Consultation = lazy(() => import("./pages/Consultation"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Author = lazy(() => import("./pages/Author"));
const Reviews = lazy(() => import("./pages/Reviews"));
const PlanningChecklist = lazy(() => import("./pages/PlanningChecklist"));
const NotFound = lazy(() => import("./pages/NotFound"));
const JoinMeeting = lazy(() => import("./pages/JoinMeeting"));

// Admin routes + their auth stack are code-split so no admin/axios/adminApi
// code ships in the initial bundle for public visitors.
const AdminRoutes = lazy(() => import("./routes/AdminRoutes"));

// Sonner Toaster is lazy so its ~34 kB (uncompressed) doesn't ship on first paint.
const LazyToaster = lazy(() =>
  import("./components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

const RouteFallback = () => (
  <div className="min-h-screen grid place-items-center bg-[hsl(30_40%_97%)] text-[hsl(var(--muted-foreground))]" aria-busy="true">Loading…</div>
);

// Fire a GA4 page_view on every React Router navigation (initial load + subsequent).
function GA4RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const page_path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}

// One lightweight delegated listener that turns Call / Text / Instant-Quote taps
// into GA4 events (site-wide, no per-component wiring). Mark these as Key Events
// in GA4 Admin to count them as conversions.
function ConversionTracker() {
  useEffect(() => {
    const onClick = (e) => {
      if (typeof window.gtag !== "function") return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      let name = null;
      let method = null;
      if (href.startsWith("tel:")) { name = "phone_call"; method = "phone"; }
      else if (href.startsWith("sms:")) { name = "sms_text"; method = "sms"; }
      else if (/djintelligence\.com\/quote/i.test(href)) { name = "quote_start"; method = "quote"; }
      if (!name) return;
      window.gtag("event", name, {
        method,
        source: a.getAttribute("data-testid") || "",
        link_url: href,
        transport_type: "beacon",
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return null;
}

function Layout() {
  return (
    <SmoothScroll>
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <MobileCtaBar />
    </SmoothScroll>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GA4RouteTracker />
        <ConversionTracker />
        <AnimatePresence mode="wait">
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Suspense fallback={<RouteFallback />}><Services /></Suspense>} />
              <Route path="/testimonials" element={<Suspense fallback={<RouteFallback />}><Testimonials /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={<RouteFallback />}><About /></Suspense>} />
              <Route path="/consultation" element={<Suspense fallback={<RouteFallback />}><Consultation /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={<RouteFallback />}><Contact /></Suspense>} />
              <Route path="/blog" element={<Suspense fallback={<RouteFallback />}><Blog /></Suspense>} />
              <Route path="/review" element={<Suspense fallback={<RouteFallback />}><Reviews /></Suspense>} />
              <Route path="/reviews" element={<Suspense fallback={<RouteFallback />}><Reviews /></Suspense>} />
              <Route path="/blog/author/:authorSlug" element={<Suspense fallback={<RouteFallback />}><Author /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={<RouteFallback />}><BlogPost /></Suspense>} />
              <Route path="/wedding-planning-checklist" element={<Suspense fallback={<RouteFallback />}><PlanningChecklist /></Suspense>} />
              <Route path="*" element={<Suspense fallback={<RouteFallback />}><NotFound /></Suspense>} />
            </Route>
            <Route
              path="/meet/:token"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <JoinMeeting />
                </Suspense>
              }
            />
            <Route
              path="/admin/*"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <AdminRoutes />
                </Suspense>
              }
            />
          </Routes>
        </AnimatePresence>
      </BrowserRouter>
      <Suspense fallback={null}>
        <LazyToaster position="top-center" richColors />
      </Suspense>
    </div>
  );
}

export default App;
